import type React from 'react';

import { type colors } from '@packages/core/styles';
import { Button, Icon, type StsIconName, Typography } from '@packages/ui/shared';
import { useEffect } from 'react';
import { Toast } from 'react-bootstrap';

/**
 * Props for the WorksheetToast component, which displays a timed toast notification
 * within the worksheet editor.
 */
export type WorksheetToastProps = {
    /** Duration in milliseconds before the toast auto-hides. Defaults to 5000. */
    autoHideTime?: number;

    /** The message or element displayed inside the toast body. */
    body: JSX.Element | string;

    /** Optional icon name displayed next to the close button. Defaults to 'x-close'. */
    iconName?: StsIconName;

    /** Called when the toast is dismissed (manually or via auto-hide). */
    onClose: () => void;

    /** Whether the toast is currently visible. */
    show: boolean;

    /** Optional inline styles merged with the toast's fixed-position container style. */
    styles?: React.CSSProperties | undefined;

    /** Color key applied to the toast body text and close icon. Defaults to 'black'. */
    textColor?: keyof typeof colors;
};

/**
 * Displays a fixed-position toast notification with auto-hide behavior.
 * Used in the worksheet editor to show success/error messages after
 * draft saves, submissions, and deletions.
 *
 * @param props - component props
 * @returns the toast notification element
 */
export function WorksheetToast({
    show,
    onClose,
    body,
    autoHideTime,
    iconName,
    styles,
    textColor = 'black',
}: WorksheetToastProps) {
    useEffect(() => {
        autoHide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    /** Schedules the toast to close after the configured auto-hide delay. */
    function autoHide() {
        setTimeout(() => {
            onClose();
        }, autoHideTime || 5000);
    }

    /** Merge the fixed positioning with any custom styles from the parent. */
    const toastStyle = { position: 'fixed' as const, top: 50, left: '40%', right: '40%', zIndex: 9999, ...styles };

    return (
        <Toast onClose={onClose} show={show} style={toastStyle}>
            <Toast.Body>
                <div className="d-flex justify-content-between align-items-center pl-2">
                    <Typography color={textColor} variant="caption">
                        {body}
                    </Typography>
                    <Button onPress={onClose} variant="ghost-gray">
                        <Icon color={textColor} name={iconName || 'x-close'} />
                    </Button>
                </div>
            </Toast.Body>
        </Toast>
    );
}
